import { t } from "@lingui/macro";
import { format, parseISO } from "date-fns";

import { RecurrenceType } from "@/features/RecurringRequests/recurrence";

export const recurringSeriesCadencePhrase = ({
  recurrenceType,
  repeatInterval,
  sourceDate,
}: {
  recurrenceType: RecurrenceType | null | undefined;
  repeatInterval: number | null | undefined;
  sourceDate?: string | null;
}) => {
  if (recurrenceType === "DAY") return t`every day`;

  if (recurrenceType === "MONTH") {
    if (!sourceDate) return t`every month`;
    return t`every month on the ${format(parseISO(sourceDate), "do")}`;
  }

  if (recurrenceType === "WEEK" && repeatInterval === 2) {
    if (!sourceDate) return t`every other week`;
    return t`every other ${format(parseISO(sourceDate), "EEEE")}`;
  }

  if (!sourceDate) return t`every week`;
  return t`every ${format(parseISO(sourceDate), "EEEE")}`;
};

const seriesPositionWord = (positionIndex: number) => {
  if (positionIndex === 0) return t`First`;
  if (positionIndex === 1) return t`Second`;
  if (positionIndex === 2) return t`Third`;

  const position = positionIndex + 1;
  const mod100 = position % 100;
  const mod10 = position % 10;
  if (mod100 < 11 || mod100 > 13) {
    if (mod10 === 1) return t`${position}st`;
    if (mod10 === 2) return t`${position}nd`;
    if (mod10 === 3) return t`${position}rd`;
  }
  return t`${position}th`;
};

/**
 * Header subtitle under the request date, e.g.
 * "First of 4 requests · every Tuesday through Sep 8th"
 */
export const recurringSeriesHeaderSubtitle = ({
  positionIndex,
  totalCount,
  recurrenceType,
  repeatInterval,
  sourceDate,
  endsDate,
}: {
  positionIndex: number;
  totalCount: number;
  recurrenceType: RecurrenceType | null | undefined;
  repeatInterval: number | null | undefined;
  sourceDate?: string | null;
  endsDate?: string | null;
}) => {
  if (totalCount < 1) return null;

  const positionLabel = seriesPositionWord(positionIndex);
  const cadence = recurringSeriesCadencePhrase({
    recurrenceType,
    repeatInterval,
    sourceDate,
  });
  const throughDate = endsDate ? format(parseISO(endsDate), "MMM do") : null;

  if (!throughDate) {
    return t`${positionLabel} of ${totalCount} requests · ${cadence}`;
  }

  return t`${positionLabel} of ${totalCount} requests · ${cadence} through ${throughDate}`;
};
